export default {
	ids: [
		'relief',
		'cube_size',
		'cube_levels',
		'cube_fill',
		'cube_split',
		'cube_split_fill',
		'cube_corner',
		'cube_cluster',
		'cube_cluster_size',
		'cube_seed',
	],
	glsl: `
// Voxel steps (relief > 0, tnWalk's loop calls vxPiece per piece): cubes standing on the section square's four sides, snapped in the tunnel's frame, across each side and along by depth between the piece's mitre planes. Each side a quadtree of cube_levels: a largest cell is empty, one cube or four half cells. Radii throughout; the section's half-size is 1.
// vxU: the smallest cube's side; vxH: the tallest cube's height; vxRows: largest cells across a side, whole, so they tile a piece (TN_PIECE) too; vxPiece: the camera's piece, counted from the start of travel.
float vxU;
float vxScale;
float vxRows;
float vxPiece;
int vxLevels;
int vxSides;

void vxSetup() {
	float big = exp2(uCubeLevels - 1.0);
	vxRows = max(floor(TN_PIECE / max(uCubeSize / TUBE_RADIUS, 1e-3) + 0.5), 1.0);
	vxU = TN_PIECE / vxRows / big;
	// The cubes sink into the wall as it leaves the tube, so the frustum's tilted walls never carry them.
	vxScale = uRelief * smoothstep(0.75, 1.0, uTunnel);
	vxH = vxScale * big * vxU;
	vxPiece = uSpeedDepthTurns;
	vxLevels = int(clamp(uCubeLevels, 1.0, 4.0));
	vxSides = vxH > 0.0 ? 4 : 0;
}

float vxHash(vec2 p) {
	uvec2 q = uvec2(ivec2(floor(p)) + ivec2(int(uCubeSeed) * 7919, 32768)) * uvec2(1597334673u, 3812015801u);
	uint n = (q.x ^ q.y) * 1597334673u;
	n ^= n >> 16;
	n *= 2246822519u;
	n ^= n >> 13;
	return float(n) / 4294967295.0;
}

// Value noise, contrast raised so a share threshold still picks clusters.
float vxNoise(vec2 x) {
	vec2 i = floor(x);
	vec2 f = fract(x);
	f = f * f * (3.0 - 2.0 * f);
	float n = mix(mix(vxHash(i + 5003.0), vxHash(i + vec2(5004.0, 5003.0)), f.x), mix(vxHash(i + vec2(5003.0, 5004.0)), vxHash(i + 5004.0), f.x), f.y);
	return clamp((n - 0.5) * 2.2 + 0.5, 0.0, 1.0);
}

vec2 vxRot(vec2 v, int s) {
	return s == 0 ? v : s == 1 ? vec2(-v.y, v.x) : s == 2 ? -v : vec2(v.y, -v.x);
}

// The leaf over side point (w across, z down the path from the camera's piece): its low corner, side in vxU, and whether a cube stands on it; hashed on whole cells counted from the start of travel, so the cubes stay put as the camera flies.
bool vxLeaf(float side, float w, float z, out vec2 lo, out float s) {
	lo = vec2(0.0);
	s = 1.0;
	bool root = true;
	// Runtime bounds keep FXC from unrolling these loops into the walk: its link time is every tab's stall.
	for (int l = vxLevels - 1; l >= 0; l--) {
		s = exp2(float(l));
		vec2 c = floor(vec2(w + 1.0, z) / (s * vxU));
		lo = c * s * vxU - vec2(1.0, 0.0);
		vec2 cw = c + vec2(0.0, vxPiece * vxRows * exp2(uCubeLevels - 1.0 - float(l)));
		vec2 seed = cw + vec2(97.0 * side, 7919.0 * float(l));
		float k = abs(lo.x + 0.5 * s * vxU);
		float fill = clamp((root ? uCubeFill : uCubeSplitFill) * mix(1.0, 2.0 * k, uCubeCorner), 0.0, 1.0);
		float r = vxHash(seed);
		if (uCubeCluster > 0.0) r = mix(r, 1.0 - vxNoise(vec2((cw.x + 0.5) * s + 31.0 * side / vxU, (cw.y + 0.5) * s) / uCubeClusterSize), uCubeCluster);
		if (r >= fill) return false;
		if (l == 0 || vxHash(seed + vec2(0.0, 31.0)) >= uCubeSplit) return true;
		root = false;
	}
	return false;
}

// The piece: joints j0, j1, mitre normals m0, m1, its number k from the camera's. Depth down the path, (k + u) pieces with u the place between the mitre planes, continuous across joints.
float vxDepth(vec3 P, vec3 j0, vec3 j1, vec3 m0, vec3 m1, float k) {
	float d0 = dot(P - j0, m0);
	float d1 = dot(j1 - P, m1);
	return (k + d0 / max(d0 + d1, 1e-6)) * TN_PIECE;
}

// The ray parameter from p where the depth is zb: the plane (1 − c)·d0 = c·d1 through the mitres' meeting line.
float vxDepthT(float zb, vec3 p, vec3 rd, vec3 j0, vec3 j1, vec3 m0, vec3 m1, float k) {
	float c = zb / TN_PIECE - k;
	float f = (1.0 - c) * dot(p - j0, m0) - c * dot(j1 - p, m1);
	float dz = dot(rd, (1.0 - c) * m0 + c * m1);
	return abs(dz) < 1e-7 ? -1e9 : -f / dz;
}

// The first cube face on side (0 right, counter-clockwise) the ray meets before tEnd, from p (section point o, direction d), leaf to leaf: its ray parameter, normal, cube side in vxU and centre's depth.
float vxSide(int side, vec3 p, vec3 rd, vec2 o, vec2 d, vec3 e1, vec3 e2, vec3 a, vec3 j0, vec3 j1, vec3 m0, vec3 m1, float pk, float tEnd, out vec3 n, out float size, out float centre) {
	vec2 q = vxRot(o, (4 - side) & 3);
	vec2 v = vxRot(d, (4 - side) & 3);
	size = 0.0;
	centre = 0.0;
	// A ray moving away from the side still meets the front faces of its cubes while it is in their band (after a bend turns the frame).
	bool toward = v.x > 1e-6;
	if (!toward && q.x < 1.0 - vxH) return 1e9;
	vec2 out2 = vxRot(vec2(1.0, 0.0), side);
	vec2 across2 = vxRot(vec2(0.0, 1.0), side);
	vec3 outN = out2.x * e1 + out2.y * e2;
	vec3 acrossN = across2.x * e1 + across2.y * e2;
	vec3 alongN = -sign(dot(rd, a)) * a;
	float t = toward ? max((1.0 - vxH - q.x) / v.x, 0.0) : 0.0;
	vec3 lastN = alongN;
	for (int i = 0; i < 96; i++) {
		if (t >= tEnd) return 1e9;
		vec2 at = q + v * t;
		float hgt = 1.0 - at.x;
		if (!toward && hgt > vxH) return 1e9;
		float z = vxDepth(p + rd * t, j0, j1, m0, m1, pk);
		vec2 lo;
		float s;
		float h = vxLeaf(float(side), at.y, z, lo, s) ? s * vxU * vxScale : 0.0;
		if (hgt < h - 1e-4) {
			n = lastN;
			size = s;
			centre = lo.y + 0.5 * s * vxU;
			return t;
		}
		float tTop = h > 0.0 && toward ? t + (hgt - h) / v.x : 1e9;
		float tOut = toward ? 1e9 : t + (vxH - hgt) / max(-v.x, 1e-7);
		float sw = s * vxU;
		float tw = abs(v.y) > 1e-7 ? t + ((v.y > 0.0 ? lo.x + sw : lo.x) - at.y) / v.y : 1e9;
		float za = vxDepthT(lo.y, p, rd, j0, j1, m0, m1, pk);
		float zb = vxDepthT(lo.y + sw, p, rd, j0, j1, m0, m1, pk);
		float tz = min(za > t + 1e-6 ? za : 1e9, zb > t + 1e-6 ? zb : 1e9);
		if (tTop <= min(tw, tz)) {
			if (tTop >= tEnd) return 1e9;
			n = -outN;
			size = s;
			centre = lo.y + 0.5 * s * vxU;
			return tTop;
		}
		if (tOut < min(tw, tz)) return 1e9;
		lastN = tw < tz ? -sign(v.y) * acrossN : alongN;
		t = min(tw, tz) + 1e-4;
	}
	return 1e9;
}

// The nearest cube face in this piece before tEnd over the four sides: its ray parameter (1e9 none), world normal, size and centre's depth down the path.
float vxPieceHit(vec3 p, vec3 rd, vec2 o, vec2 d, vec3 e1, vec3 e2, vec3 a, vec3 j0, vec3 j1, vec3 m0, vec3 m1, float pk, float tEnd, out vec3 n, out float size, out float centre) {
	float best = 1e9;
	size = 0.0;
	centre = 0.0;
	// The walk enters a piece exactly on its mitre plane, where a cell lookup can fall in the piece before: start just inside.
	const float IN = 1e-4;
	p += rd * IN;
	o += d * IN;
	for (int side = 0; side < vxSides; side++) {
		vec3 sn;
		float ss;
		float sc;
		float tc = vxSide(side, p, rd, o, d, e1, e2, a, j0, j1, m0, m1, pk, min(best, tEnd - IN), sn, ss, sc);
		if (tc < best) {
			best = tc;
			n = sn;
			size = ss;
			centre = sc;
		}
	}
	return best < 1e8 ? best + IN : best;
}

Surf relief(Ray ray, Surf s) {
	return s;
}
`,
}
